import React, { useMemo } from 'react';
import {
  type Control,
  type FieldArrayWithId,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
} from 'react-hook-form';
import { X } from 'lucide-react';
import { RiskProcessAssociations } from '../../risks/components/RiskProcessAssociations.tsx';
import { ControlMappedProcessesField } from './ControlMappedProcessesField.tsx';
import type { OptionLabel } from '../../../types/options.ts';
import { Tooltip, TooltipProvider } from '../../../admin/components/shared/Tooltip.tsx';
import { LINE_CLAMP_3_CLASS } from '../../risks/utils/riskDetailsUtils.ts';

import type { ControlRiskAssociationsFormValues } from '../schema.ts';
import {
  extractRiskMappingDisplayRiskId,
  extractRiskMappingRiskUuid,
  findRiskMappingRowForFormRiskId,
} from '../../../request/controls/controlRiskMappings.ts';

interface ControlAssociationsEditorProps {
  control: Control<ControlRiskAssociationsFormValues>;
  fields: FieldArrayWithId<ControlRiskAssociationsFormValues, 'riskAssociations', 'id'>[];
  append: UseFieldArrayAppend<ControlRiskAssociationsFormValues, 'riskAssociations'>;
  remove: UseFieldArrayRemove;
  riskOptions: OptionLabel[];
  riskLabelsByValue: Record<string, string>;
  loadingRisks: boolean;
  loadingMoreRisks: boolean;
  riskSearchInput: string;
  hasNextRiskPage: boolean;
  onRiskSearchChange: (term: string) => void;
  onLoadMoreRisks: () => void;
  processLabelsByValue: Record<string, string>;
  riskMappings: Array<Record<string, unknown>>;
  readOnly?: boolean;
  onProcessOptionsChange?: (riskId: string, options: OptionLabel[]) => void;
  hideProcessSection?: boolean;
  maxSelectedRisks?: number;
  auditYearId?: string | null;
}

function resolveRiskLabel(
  id: string,
  riskOptions: OptionLabel[],
  riskLabelsByValue: Record<string, string>
): string {
  return riskLabelsByValue[id] ?? riskOptions.find((o) => o.value === id)?.label ?? id;
}

export const ControlAssociationsEditor: React.FC<ControlAssociationsEditorProps> = ({
  control,
  fields,
  append,
  remove,
  riskOptions,
  riskLabelsByValue,
  loadingRisks,
  loadingMoreRisks,
  riskSearchInput,
  hasNextRiskPage,
  onRiskSearchChange,
  onLoadMoreRisks,
  processLabelsByValue,
  riskMappings,
  readOnly = false,
  onProcessOptionsChange,
  hideProcessSection = false,
  maxSelectedRisks,
  auditYearId,
}) => {
  const selectedRiskIds = useMemo(() => fields.map((f) => f.riskId), [fields]);

  /** Block picker options for every id alias of an already-linked risk (UUID vs RSK_xxx). */
  const excludedRiskOptionValues = useMemo(() => {
    const blocked = new Set<string>(selectedRiskIds);
    for (const riskId of selectedRiskIds) {
      const mapping = findRiskMappingRowForFormRiskId(riskMappings, riskId);
      if (!mapping) continue;
      for (const key of [
        String(mapping.id ?? '').trim(),
        extractRiskMappingRiskUuid(mapping),
        extractRiskMappingDisplayRiskId(mapping),
        String(mapping.risk_id ?? '').trim(),
        String(mapping.client_risk_id ?? '').trim(),
      ]) {
        if (key) blocked.add(key);
      }
    }
    for (const opt of riskOptions) {
      for (const riskId of selectedRiskIds) {
        const label = riskLabelsByValue[riskId];
        if (label && opt.label === label) {
          blocked.add(opt.value);
        }
      }
    }
    return blocked;
  }, [riskLabelsByValue, riskMappings, riskOptions, selectedRiskIds]);

  return (
    <TooltipProvider>
      <div>
        <RiskProcessAssociations
          variant="risk"
          pickerOnly
          readOnly={readOnly}
          selectedValues={Array.from(excludedRiskOptionValues)}
          processOptions={riskOptions}
          selectedLabelsByValue={riskLabelsByValue}
          loadingOptions={loadingRisks}
          loadingMoreOptions={loadingMoreRisks}
          searchInputValue={riskSearchInput}
          hasMoreOptions={hasNextRiskPage}
          onSearchChange={onRiskSearchChange}
          onLoadMore={onLoadMoreRisks}
          placeholderOnBlur="+ Add risk..."
          placeholderOnFocus="Search risks..."
          optionLineClamp={2}
          onAdd={(value) => {
            if (readOnly) return;
            if (excludedRiskOptionValues.has(value)) return;
            if (maxSelectedRisks === 1) {
              if (fields.length === 0) {
                append({ riskId: value, mitigatingProcessIds: [] });
                return;
              }
              remove(0);
            }
            append({ riskId: value, mitigatingProcessIds: [] });
          }}
          onRemove={(value) => {
            if (readOnly) return;
            const idx = fields.findIndex((f) => f.riskId === value);
            if (idx >= 0) remove(idx);
          }}
        />

        <div className="mt-3 space-y-3">
          {fields.map((fieldRow, index) => {
            const title = resolveRiskLabel(fieldRow.riskId, riskOptions, riskLabelsByValue);
            return (
              <div
                key={fieldRow.id}
                className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm sm:px-4"
              >
                <div className="flex items-stretch gap-0">
                  <div className="min-w-0 flex-1 pr-3 sm:pr-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <Tooltip content={title}>
                        <div className="w-full min-w-0">
                          <p className={`text-xs font-semibold text-slate-800 ${LINE_CLAMP_3_CLASS}`}>
                            {title}
                          </p>
                        </div>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-stretch">
                    {!readOnly ? (
                      <div className="flex items-center pr-2.5 sm:pr-3">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-500 shadow-sm hover:bg-rose-100"
                          title="Remove risk association"
                          aria-label="Remove risk association"
                        >
                          <X className="h-3 w-3 stroke-[2.75]" aria-hidden />
                        </button>
                      </div>
                    ) : null}
                    <div
                      aria-hidden
                      className="my-[18px] w-0 shrink-0 self-stretch border-l border-dotted border-[#E0E0E0]"
                    />
                    <div className="w-3 shrink-0 sm:w-4" aria-hidden />
                  </div>

                  {!hideProcessSection ? (
                    <div className="min-w-0 flex-1 pl-0 sm:pl-0">
                      <ControlMappedProcessesField
                        riskId={fieldRow.riskId}
                        control={control}
                        index={index}
                        processLabelsByValue={processLabelsByValue}
                        riskMappings={riskMappings}
                        riskOptions={riskOptions}
                        readOnly={readOnly}
                        onProcessOptionsChange={onProcessOptionsChange}
                        auditYearId={auditYearId}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};
